import { useState } from "react";
import { NavLink, useParams } from "react-router-dom";
import { z } from "zod";
import { useApi } from "../api/hooks";
import { useSession } from "../auth/Session";
import { SelecteurPeriode } from "../composants/Tableau";
import { GROUPES, TOUS_LES_ECRANS } from "../routes/navigation";
import { ECRANS } from "../routes/ecrans";

const EnTeteSchema = z.object({
  id: z.string(),
  raisonSociale: z.string(),
  regime: z.string(),
  actif: z.boolean(),
  estDemonstration: z.boolean(),
});

/**
 * §5.D — Fiche client : les 22 écrans, dans leurs six groupes.
 *
 * ⛔ Le « Suivi en direct » et les notes internes ne s'affichent NULLE PART
 * ailleurs que sur l'espace du gérant. Aucun canal vers le portail client.
 */
export function FicheClient() {
  const { dossierId = "", ecran = "tableau-de-bord" } = useParams();
  const { session, deconnecter } = useSession();
  const estGerant = session?.role === "admin";

  const maintenant = new Date();
  const [annee, setAnnee] = useState(maintenant.getFullYear());
  const [mois, setMois] = useState(maintenant.getMonth() + 1);

  const entete = useApi(["dossier-entete", dossierId], `/dossiers/${dossierId}`, EnTeteSchema);

  const courant = TOUS_LES_ECRANS.find((e) => e.cle === ecran);
  const Composant = ECRANS[ecran];
  const autorise = courant && Composant && (!courant.gerantSeul || estGerant);

  return (
    <div className="fiche-client">
      <nav className="fiche-client__nav">
        <NavLink to="/dossiers" className="retour">
          Retour aux dossiers
        </NavLink>

        <div className="fiche-client__client">
          <strong>{entete.data?.raisonSociale ?? "…"}</strong>
          {entete.data && (
            <span className="fiche-client__regime">
              {entete.data.regime}
              {entete.data.estDemonstration && " · démonstration"}
              {!entete.data.actif && " · désactivé"}
            </span>
          )}
        </div>

        {GROUPES.map((g) => {
          const ecrans = g.ecrans.filter((e) => !e.gerantSeul || estGerant);
          if (ecrans.length === 0) return null;

          return (
            <section key={g.titre}>
              <h3>{g.titre}</h3>
              <ul>
                {ecrans.map((e) => (
                  <li key={e.cle}>
                    <NavLink to={`/dossiers/${dossierId}/${e.cle}`} end title={e.resume}>
                      {e.titre}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </section>
          );
        })}

        <section>
          <h3>Documentaire</h3>
          <ul>
            <li>
              <NavLink to={`/dossiers/${dossierId}/transmissions`} end>
                Transmissions
              </NavLink>
            </li>
          </ul>
        </section>

        <button type="button" className="deconnexion" onClick={deconnecter}>
          Se déconnecter
        </button>
      </nav>

      <main className="fiche-client__contenu">
        {!autorise ? (
          <p className="alerte">
            {courant?.gerantSeul && !estGerant
              ? "Cet écran est réservé au gérant."
              : "Écran inconnu."}
          </p>
        ) : (
          <>
            <header className="ecran-entete">
              <div>
                <h1>{courant.titre}</h1>
                <p className="resume">{courant.resume}</p>
              </div>
              <SelecteurPeriode
                annee={annee}
                mois={mois}
                setAnnee={setAnnee}
                setMois={setMois}
              />
            </header>

            <Composant
              dossierId={dossierId}
              annee={annee}
              mois={mois}
              estGerant={estGerant}
            />
          </>
        )}
      </main>
    </div>
  );
}
